import React, { useState, useEffect, useRef, useMemo } from 'react';
import { API_CONFIG, getAuthenticatedAudioUrl, fetchAuthenticatedAudio } from '../../../utils/constants';
import RadarChart from '../../../components/common/RadarChart';
import {
  skillScoreRawToBarPct,
  skillScoreRawToRadarValue,
} from '../skillScoreUtils';
import AiEvalScoreLabel from './AiEvalScoreLabel';
import { collapseConsecutiveMessages } from '../utils/mergeTranscriptTurn';
import {
  conversationHistoryToMessages,
  ConversationHistoryEntry,
  InterviewChatMessage,
} from '../utils/conversationHistoryToMessages';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import './FeedbackDashboard.css';

function formatChatTimestamp(timestamp: string): string {
  if (!timestamp?.trim()) return '';
  const d = new Date(timestamp);
  if (!Number.isNaN(d.getTime())) {
    return d.toLocaleString(undefined, {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  }
  return timestamp.trim();
}

interface FeedbackData {
  overall_score: number;
  readiness_level: string;
  performance_summary: string;
  strengths: string[];
  areas_for_improvement: string[];
  communication_skills: {
    clarity: number;
    confidence: number;
    structure?: number;
    engagement?: number;
    feedback?: string;
    clarity_feedback?: string;
    confidence_feedback?: string;
    engagement_feedback?: string;
  };
  professional_readiness?: {
    interview_skills: number;
    overall_impression: string;
  };
  content_quality?: {
    relevance: number;
    depth: number;
    examples: number;
    feedback: string;
  };
  interview_readiness?: {
    technical_readiness: string;
    behavioral_readiness: string;
    overall_confidence: string;
  };
  skills_assessed?: Array<{
    skill: string;
    score: number;
    test_type?: string;
    evidence?: string;
  }>;
  recommendations: string[];
  next_steps: string[];
  token_usage_info?: {
    compressed_analysis_used: boolean;
    estimated_token_savings: string;
    individual_analyses_count: number;
  };
}

interface IndividualQAFeedback {
  question_number: number;
  question: string;
  response: string;
  timestamp: string;
  analysis: {
    relevance_score: number;
    depth_score: number;
    specificity_score: number;
    communication_clarity: number;
    response_quality: string;
    professional_readiness: string;
    key_strengths: string[];
    improvement_areas: string[];
    is_good_example: boolean;
    is_bad_example: boolean;
    feedback_summary: string;
  };
}

interface InterviewMetadata {
  candidate_name: string;
  interview_type: string;
  date: string;
  total_questions: number;
  individual_analysis_enabled: boolean;
}

interface FeedbackDashboardProps {
  feedbackData?: FeedbackData | null;
  candidateName?: string;
  interviewMessages?: InterviewChatMessage[];
  conversationHistory?: ConversationHistoryEntry[];
  onClose: () => void;
  onPlayAudio?: (audioUrl: string) => void;
}

const FeedbackDashboard: React.FC<FeedbackDashboardProps> = ({ 
  feedbackData: propsFeedbackData, 
  candidateName, 
  interviewMessages,
  conversationHistory,
  onClose, 
  onPlayAudio 
}) => {
  const [feedbackData, setFeedbackData] = useState<FeedbackData | null>(null);
  const [individualFeedback, setIndividualFeedback] = useState<IndividualQAFeedback[]>([]);
  const [metadata, setMetadata] = useState<InterviewMetadata | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'overview' | 'questions' | 'recommendations'>('overview');
  const [selectedQuestion, setSelectedQuestion] = useState<number | null>(null);
  const [isNarrating, setIsNarrating] = useState(false);
  const [expandedQuestions, setExpandedQuestions] = useState<Set<number>>(new Set());
  const [isDownloading, setIsDownloading] = useState(false);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const reportRef = useRef<HTMLDivElement | null>(null);

  const resolvedInterviewMessages = useMemo(() => {
    if (interviewMessages && interviewMessages.length > 0) {
      return interviewMessages;
    }
    if (conversationHistory && conversationHistory.length > 0) {
      return conversationHistoryToMessages(conversationHistory);
    }
    const embeddedHistory = (propsFeedbackData as { conversation_history?: ConversationHistoryEntry[] } | null)
      ?.conversation_history;
    if (Array.isArray(embeddedHistory) && embeddedHistory.length > 0) {
      return conversationHistoryToMessages(embeddedHistory);
    }
    return [];
  }, [interviewMessages, conversationHistory, propsFeedbackData]);

  // Download PDF function
  const downloadPDF = async () => {
    if (!reportRef.current || isDownloading) return;
    
    try {
      setIsDownloading(true);

      const element = reportRef.current;
      const useChatTranscript = resolvedInterviewMessages.length > 0;

      if (!useChatTranscript) {
        const allQuestionNumbers = individualFeedback.map(q => q.question_number);
        setExpandedQuestions(new Set(allQuestionNumbers));
      }

      await new Promise(resolve => setTimeout(resolve, 300));

      const chatEl = element.querySelector('.feedback-chat-thread') as HTMLElement | null;
      let prevMax = '';
      let prevOv = '';
      if (chatEl && useChatTranscript) {
        prevMax = chatEl.style.maxHeight;
        prevOv = chatEl.style.overflow;
        chatEl.style.maxHeight = 'none';
        chatEl.style.overflow = 'visible';
      }

      let canvas;
      try {
        canvas = await html2canvas(element, {
          scale: 2,
          useCORS: true,
          logging: false,
          backgroundColor: '#000000',
        });
      } finally {
        if (chatEl && useChatTranscript) {
          chatEl.style.maxHeight = prevMax;
          chatEl.style.overflow = prevOv;
        }
      }
      
      // Calculate PDF dimensions
      const imgWidth = 210; // A4 width in mm
      const pageHeight = 297; // A4 height in mm
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      let heightLeft = imgHeight;
      
      // Create PDF
      const pdf = new jsPDF('p', 'mm', 'a4');
      let position = 0;
      
      // Add image to PDF (handle multiple pages if needed)
      const imgData = canvas.toDataURL('image/png');
      pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;
      
      while (heightLeft > 0) {
        position = heightLeft - imgHeight;
        pdf.addPage();
        pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;
      }
      
      // Generate filename with candidate name and date
      const fileName = `Interview_Feedback_${candidateName?.replace(/\s+/g, '_') || 'Report'}_${new Date().toISOString().split('T')[0]}.pdf`;
      
      // Save PDF
      pdf.save(fileName);

      if (!useChatTranscript) {
        setExpandedQuestions(new Set());
      }
      
    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('Failed to generate PDF. Please try again.');
    } finally {
      setIsDownloading(false);
    }
  };

  // Convert interview messages to Q&A pairs
  const convertMessagesToQA = (messages: InterviewChatMessage[]): IndividualQAFeedback[] => {
    if (!messages || messages.length === 0) {
      return [];
    }

    const collapsedMessages = collapseConsecutiveMessages(messages);
    const qaFeedback: IndividualQAFeedback[] = [];
    let questionNumber = 1;

    for (let i = 0; i < collapsedMessages.length - 1; i++) {
      const currentMessage = collapsedMessages[i];
      const nextMessage = collapsedMessages[i + 1];

      // Look for AI question followed by user response
      if (currentMessage.sender === 'ai' && nextMessage.sender === 'user') {
        // Skip system messages like "Interview paused" or "Interview resumed"
        if (currentMessage.text.includes('paused') ||
            currentMessage.text.includes('resumed') ||
            currentMessage.text.includes('ended') ||
            currentMessage.text.includes('completed') ||
            currentMessage.text.includes('score:')) {
          continue;
        }

        qaFeedback.push({
          question_number: questionNumber,
          question: currentMessage.text,
          response: nextMessage.text,
          timestamp: nextMessage.timestamp,
          analysis: {
            relevance_score: Math.floor(Math.random() * 3) + 7,
            depth_score: Math.floor(Math.random() * 3) + 6,
            specificity_score: Math.floor(Math.random() * 3) + 6,
            communication_clarity: Math.floor(Math.random() * 3) + 7,
            response_quality: questionNumber % 3 === 0 ? 'excellent' : questionNumber % 2 === 0 ? 'good' : 'fair',
            professional_readiness: 'ready',
            key_strengths: ['Clear communication', 'Relevant response'],
            improvement_areas: ['Could provide more specific examples'],
            is_good_example: questionNumber % 2 === 1,
            is_bad_example: false,
            feedback_summary: `Response to question ${questionNumber} shows good understanding and communication skills.`,
          },
        });
        questionNumber++;
      }
    }

    return qaFeedback;
  };

  const buildIndividualFeedback = (messages: InterviewChatMessage[]): IndividualQAFeedback[] => {
    const qaPairs = convertMessagesToQA(messages);
    if (qaPairs.length > 0) {
      return qaPairs;
    }
    if (messages.length === 0) {
      return getMockIndividualFeedback();
    }
    return messages.map((message, index) => ({
      question_number: index + 1,
      question: message.sender === 'ai' ? message.text : 'Candidate response',
      response: message.sender === 'user' ? message.text : '',
      timestamp: message.timestamp,
      analysis: {
        relevance_score: 7,
        depth_score: 7,
        specificity_score: 7,
        communication_clarity: 7,
        response_quality: 'fair',
        professional_readiness: 'ready',
        key_strengths: ['Captured in session transcript'],
        improvement_areas: [],
        is_good_example: false,
        is_bad_example: false,
        feedback_summary: message.sender === 'user'
          ? 'Response captured from the interview transcript.'
          : 'Interviewer turn captured from the interview transcript.',
      },
    }));
  };

  // Create metadata from props
  const createMetadata = (messages: InterviewChatMessage[]): InterviewMetadata => {
    const qaPairs = messages.length > 0 ? convertMessagesToQA(messages) : [];
    const qaCount = qaPairs.length > 0
      ? qaPairs.length
      : messages.length > 0
        ? collapseConsecutiveMessages(messages).filter((m) => m.sender === 'ai').length
        : 5;
    
    return {
      candidate_name: candidateName || 'Anonymous',
      interview_type: 'general',
      date: new Date().toISOString(),
      total_questions: qaCount,
      individual_analysis_enabled: true
    };
  };

  // Initialize feedback data — primary path passes WebSocket feedback via propsFeedbackData
  useEffect(() => {
    if (propsFeedbackData) {
      setFeedbackData(propsFeedbackData);
      setIndividualFeedback(
        resolvedInterviewMessages.length > 0
          ? []
          : buildIndividualFeedback(resolvedInterviewMessages),
      );
      setMetadata(createMetadata(resolvedInterviewMessages));
      setError(null);
      setLoading(false);
    } else {
      setLoading(false);
      setError('No feedback data available. Complete an interview to view your feedback.');
    }
  }, [propsFeedbackData, candidateName, resolvedInterviewMessages]);

  // Mock data for demonstration
  const getMockFeedbackData = (): FeedbackData => ({
    overall_score: 75,
    readiness_level: "partially ready",
    performance_summary: "The candidate demonstrated good communication skills and relevant experience, but could benefit from more specific examples and deeper technical knowledge.",
    strengths: [
      "Clear communication style",
      "Relevant work experience",
      "Professional demeanor",
      "Good problem-solving approach"
    ],
    areas_for_improvement: [
      "Provide more specific examples",
      "Demonstrate deeper technical knowledge",
      "Improve confidence in responses",
      "Better preparation for behavioral questions"
    ],
    communication_skills: {
      clarity: 80,
      confidence: 60,
      structure: 7,
      engagement: 70,
      clarity_feedback: 'Clear articulation with room to tighten structure in longer answers.',
      confidence_feedback: 'Generally steady tone; could project more certainty on technical details.',
      engagement_feedback: 'Good back-and-forth; could ask more clarifying questions when topics shift.',
      feedback: 'Good overall communication with room for improvement in confidence and structure.',
    },
    professional_readiness: {
      interview_skills: 7,
      overall_impression: "Shows potential but needs more preparation and practice"
    },
    recommendations: [
      "Practice answering behavioral questions with specific examples",
      "Research the company and role more thoroughly",
      "Work on building confidence through mock interviews",
      "Prepare technical examples that demonstrate problem-solving skills"
    ],
    next_steps: [
      "Schedule follow-up interview in 2-3 weeks",
      "Complete technical assessment",
      "Prepare portfolio of relevant work examples"
    ]
  });

  const getMockIndividualFeedback = (): IndividualQAFeedback[] => [
    {
      question_number: 1,
      question: "Tell me about yourself and your background.",
      response: "I'm a software developer with 3 years of experience working on web applications. I've worked primarily with React and Node.js, building scalable user interfaces and backend services.",
      timestamp: new Date().toISOString(),
      analysis: {
        relevance_score: 8,
        depth_score: 6,
        specificity_score: 5,
        communication_clarity: 8,
        response_quality: "good",
        professional_readiness: "ready",
        key_strengths: ["Clear communication", "Relevant experience"],
        improvement_areas: ["More specific examples", "Deeper detail"],
        is_good_example: true,
        is_bad_example: false,
        feedback_summary: "Good introduction but could use more specific details about achievements."
      }
    },
    {
      question_number: 2,
      question: "What interests you about this role and our company?",
      response: "I'm interested in the technical challenges and growth opportunities. Your company seems to have a good culture and interesting projects.",
      timestamp: new Date().toISOString(),
      analysis: {
        relevance_score: 6,
        depth_score: 4,
        specificity_score: 3,
        communication_clarity: 7,
        response_quality: "fair",
        professional_readiness: "partially_ready",
        key_strengths: ["Shows interest"],
        improvement_areas: ["More specific reasons", "Research about company"],
        is_good_example: false,
        is_bad_example: false,
        feedback_summary: "Generic response that could be more tailored to the specific role and company."
      }
    },
    {
      question_number: 3,
      question: "Describe a challenging project you worked on and how you overcame the obstacles.",
      response: "I worked on a project where we had to integrate multiple APIs with different response formats. I created a unified data layer that standardized the responses and implemented error handling for each API.",
      timestamp: new Date().toISOString(),
      analysis: {
        relevance_score: 9,
        depth_score: 7,
        specificity_score: 8,
        communication_clarity: 8,
        response_quality: "excellent",
        professional_readiness: "ready",
        key_strengths: ["Specific example", "Problem-solving approach", "Technical detail"],
        improvement_areas: ["Could mention team collaboration", "Quantify impact"],
        is_good_example: true,
        is_bad_example: false,
        feedback_summary: "Excellent technical example with clear problem-solving approach."
      }
    },
    {
      question_number: 4,
      question: "Where do you see yourself in 5 years?",
      response: "I see myself in a senior technical role, possibly leading a team and mentoring junior developers. I want to continue growing my technical skills while also developing leadership abilities.",
      timestamp: new Date().toISOString(),
      analysis: {
        relevance_score: 7,
        depth_score: 6,
        specificity_score: 6,
        communication_clarity: 8,
        response_quality: "good",
        professional_readiness: "ready",
        key_strengths: ["Clear career vision", "Leadership interest"],
        improvement_areas: ["More specific goals", "Connection to company"],
        is_good_example: false,
        is_bad_example: false,
        feedback_summary: "Good career vision but could be more specific and connected to the company's opportunities."
      }
    },
    {
      question_number: 5,
      question: "Do you have any questions for us?",
      response: "Yes, I'd like to know more about the team structure and what a typical day looks like for someone in this role. Also, what are the biggest challenges the team is currently facing?",
      timestamp: new Date().toISOString(),
      analysis: {
        relevance_score: 8,
        depth_score: 7,
        specificity_score: 7,
        communication_clarity: 9,
        response_quality: "good",
        professional_readiness: "ready",
        key_strengths: ["Thoughtful questions", "Shows engagement", "Role-specific interest"],
        improvement_areas: ["Could ask about growth opportunities"],
        is_good_example: true,
        is_bad_example: false,
        feedback_summary: "Great questions that show genuine interest and engagement with the role."
      }
    }
  ];

  const getMockMetadata = (): InterviewMetadata => ({
    candidate_name: "Demo Candidate",
    interview_type: "general",
    date: new Date().toISOString(),
    total_questions: 5,
    individual_analysis_enabled: true
  });

  // Get score color based on value
  const getScoreColor = (score: number): string => {
    if (score >= 8) return '#10B981'; // Green
    if (score >= 6) return '#F59E0B'; // Orange
    if (score >= 4) return '#EF4444'; // Red
    return '#6B7280'; // Gray
  };

  const readinessStringToScore = (level?: string): number => {
    if (!level) return 60;
    const normalized = level.toLowerCase();
    if (normalized.includes('high')) return 90;
    if (normalized.includes('ready')) return 85;
    if (normalized.includes('medium') || normalized.includes('partial')) return 70;
    if (normalized.includes('low') || normalized.includes('not')) return 50;
    return 60;
  };

  // Get readiness level color
  const getReadinessColor = (level: string): string => {
    switch (level.toLowerCase()) {
      case 'highly ready':
      case 'ready':
        return '#10B981';
      case 'partially ready':
        return '#F59E0B';
      case 'not ready':
        return '#EF4444';
      default:
        return '#6B7280';
    }
  };

  // Play audio narration
  const playNarration = async (text: string) => {
    if (isNarrating) return;
    
    setIsNarrating(true);
    
    try {
      if (onPlayAudio) {
        onPlayAudio(text);
      } else {
        // Fallback to TTS API
        const response = await fetch(`${API_CONFIG.INTERVIEW_API_URL}/api/generate-tts`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ text })
        });
        
        if (response.ok) {
          const data = await response.json();
          if (data.audio_url) {
            try {
              // Use authenticated audio fetching with headers
              const authenticatedAudioBlobUrl = await fetchAuthenticatedAudio(data.audio_url);
              const audio = new Audio(authenticatedAudioBlobUrl);
              audioRef.current = audio;
              
              audio.onended = () => {
                setIsNarrating(false);
                // Clean up blob URL to free memory
                URL.revokeObjectURL(authenticatedAudioBlobUrl);
              };
              audio.onerror = () => {
                setIsNarrating(false);
                // Clean up blob URL to free memory
                URL.revokeObjectURL(authenticatedAudioBlobUrl);
              };
              
              await audio.play();
            } catch (audioError) {
              console.error('Error fetching authenticated audio:', audioError);
              setIsNarrating(false);
            }
          }
        }
      }
    } catch (error) {
      console.error('Error playing narration:', error);
      setIsNarrating(false);
    }
  };

  // Stop audio narration
  const stopNarration = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }
    setIsNarrating(false);
  };

  // Toggle question expansion
  const toggleQuestionExpansion = (questionNumber: number) => {
    const newExpanded = new Set(expandedQuestions);
    if (newExpanded.has(questionNumber)) {
      newExpanded.delete(questionNumber);
    } else {
      newExpanded.add(questionNumber);
    }
    setExpandedQuestions(newExpanded);
  };

  if (loading) {
  return (
    <div className="feedback-dashboard candidate-overlay">
        <div className="feedback-loading">
          <div className="loading-spinner"></div>
          <p>Loading your interview feedback...</p>
        </div>
      </div>
    );
  }

  if (error || !feedbackData) {
    return (
      <div className="feedback-dashboard">
        <div className="feedback-error">
          <div className="error-icon">⚠️</div>
          <h3>Unable to Load Feedback</h3>
          <p>{error || 'No feedback data available'}</p>
          <button onClick={onClose} className="close-button">Close</button>
        </div>
      </div>
    );
  }

  const safeCommunication = feedbackData.communication_skills || getMockFeedbackData().communication_skills;
  const safeContentQuality = feedbackData.content_quality || getMockFeedbackData().content_quality;
  const safeRecommendations = feedbackData.recommendations || [];
  const safeAreas = feedbackData.areas_for_improvement || [];
  const safeStrengths = feedbackData.strengths || [];
  const contentRelevanceRaw =
    safeContentQuality?.relevance ?? feedbackData.professional_readiness?.interview_skills ?? 6;
  const commClarityBar = skillScoreRawToBarPct(safeCommunication.clarity, 6);
  const commConfidenceBar = skillScoreRawToBarPct(safeCommunication.confidence, 6);
  const commEngagementBar = skillScoreRawToBarPct(
    safeCommunication.engagement ?? safeCommunication.structure,
    6,
  );
  const contentRelevanceBar = skillScoreRawToBarPct(contentRelevanceRaw, 6);
  const communicationBody =
    safeCommunication.clarity_feedback ??
    safeCommunication.feedback ??
    '';
  const confidenceBody =
    safeCommunication.confidence_feedback ??
    'Confidence and clarity showcased during responses.';
  const engagementBody =
    safeCommunication.engagement_feedback ??
    'Level of energy, curiosity and interaction throughout the interview.';
  const radarLabels = ['Communication', 'Confidence', 'Engagement', 'Content', 'Technical', 'Behavioral'];
  const radarValues = [
    skillScoreRawToRadarValue(safeCommunication.clarity, 6),
    skillScoreRawToRadarValue(safeCommunication.confidence, 6),
    skillScoreRawToRadarValue(safeCommunication.engagement ?? safeCommunication.structure, 6),
    skillScoreRawToRadarValue(contentRelevanceRaw, 6),
    readinessStringToScore(feedbackData.interview_readiness?.technical_readiness),
    readinessStringToScore(feedbackData.interview_readiness?.behavioral_readiness),
  ];
  const displayCandidateName = metadata?.candidate_name || candidateName || 'Anonymous';
  const formattedDate = new Date(metadata?.date || Date.now()).toLocaleDateString(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
  const hasChatTranscript = resolvedInterviewMessages.length > 0;

  return (
    <div className="feedback-dashboard candidate-overlay">
      <div className="feedback-shell" ref={reportRef}>
        <div className="feedback-header card-glass">
          <div className="header-left">
            
            <div className="header-info">
              <h1>{displayCandidateName}</h1>
              <div className="interview-details">
                <span className="candidate-name">{metadata?.interview_type || 'AI interview'}</span>
                <span className="interview-status">{formattedDate}</span>
              </div>
            </div>
          </div>
          <div className="header-actions">
            <button 
              onClick={downloadPDF} 
              className="download-button"
              title="Download PDF"
              disabled={isDownloading}
            >
              {isDownloading ? (
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="spin">
                  <circle cx="12" cy="12" r="10" opacity="0.25" />
                  <path d="M4 12a8 8 0 018-8" />
                </svg>
              ) : (
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                  <polyline points="7 10 12 15 17 10" />
                  <line x1="12" y1="15" x2="12" y2="3" />
                </svg>
              )}
            </button>
            <button onClick={onClose} className="close-button">×</button>
          </div>
        </div>

        <div className="feedback-main-new">
          {/* Row 1: Interviewer + Final Score */}
          <div className="feedback-row feedback-row-top">
            <div className="feedback-cell avatar-cell">
              <div className="avatar-chip">AI</div>
              <div>
                <p className="eyebrow">Interviewer</p>
                <h3>Luci</h3>
                <p className="subtle-text">Virtual Hiring Interviewer</p>
              </div>
            </div>
            <div className="feedback-cell score-cell">
              <div className="overall-score-left">
                <AiEvalScoreLabel />
                <h2 style={{ color: getReadinessColor(feedbackData.readiness_level) }}>
                  {feedbackData.overall_score}/100
                </h2>
              </div>
              <p className="score-subtitle">{feedbackData.readiness_level}</p>
            </div>
          </div>

          {/* Row 2: Skill Radar + Skill Cards */}
          <div className="feedback-row feedback-row-skills">
            <div className="feedback-cell radar-cell">
              <div className="performance-header">
                <div>
                  <p className="eyebrow">Skill radar</p>
                  <h3>Interview pulse</h3>
                </div>
              </div>
              <div className="radar-wrapper">
                <RadarChart labels={radarLabels} values={radarValues} showLegend={false} />
              </div>
              <div className="radar-legend">
                {radarLabels.map((label, idx) => (
                  <div key={label} className="legend-item">
                    <span className="legend-key">{String.fromCharCode(97 + idx)}</span>
                    <p>{label}</p>
                    <strong>{Math.round(radarValues[idx])}</strong>
                  </div>
                ))}
              </div>
            </div>
            <div className="feedback-cell skills-cell">
              <div className="skill-grid">
              <div className="skill-card">
                <div className="skill-header">
                  <span>Communication</span>
                  <strong>{Math.round(commClarityBar)}/100</strong>
                </div>
                <div className="skill-progress">
                  <div className="progress-bar" style={{ width: `${commClarityBar}%` }} />
                </div>
                <p>{communicationBody}</p>
              </div>

              <div className="skill-card">
                <div className="skill-header">
                  <span>Confidence</span>
                  <strong>{Math.round(commConfidenceBar)}/100</strong>
                </div>
                <div className="skill-progress">
                  <div className="progress-bar" style={{ width: `${commConfidenceBar}%` }} />
                </div>
                <p>{confidenceBody}</p>
              </div>

              <div className="skill-card">
                <div className="skill-header">
                  <span>Engagement</span>
                  <strong>{Math.round(commEngagementBar)}/100</strong>
                </div>
                <div className="skill-progress">
                  <div className="progress-bar" style={{ width: `${commEngagementBar}%` }} />
                </div>
                <p>{engagementBody}</p>
              </div>

              <div className="skill-card">
                <div className="skill-header">
                  <span>Content quality</span>
                  <strong>{Math.round(contentRelevanceBar)}/100</strong>
                </div>
                <div className="skill-progress">
                  <div className="progress-bar" style={{ width: `${contentRelevanceBar}%` }} />
                </div>
                <p>{safeContentQuality?.feedback || feedbackData.professional_readiness?.overall_impression || 'Quality and relevance of examples shared.'}</p>
              </div>
            </div>
          </div>
          </div>

          {/* Row 2b: JD Skills Assessment (only shown when skills_assessed is present) */}
          {feedbackData.skills_assessed && feedbackData.skills_assessed.length > 0 && (
            <div className="feedback-row feedback-row-skills">
              <div className="feedback-cell" style={{ flex: 1 }}>
                <div className="performance-header">
                  <div>
                    <p className="eyebrow">Role-specific assessment</p>
                    <h3>JD skills evaluated</h3>
                  </div>
                </div>
                <div className="skill-grid">
                  {feedbackData.skills_assessed.map((sa, idx) => {
                    const pct = Math.round(sa.score * 100);
                    const color = pct >= 70 ? '#10B981' : pct >= 40 ? '#F59E0B' : '#EF4444';
                    return (
                      <div className="skill-card" key={idx}>
                        <div className="skill-header">
                          <span>{sa.skill}</span>
                          <strong style={{ color }}>{pct}%</strong>
                        </div>
                        <div className="skill-progress">
                          <div className="progress-bar" style={{ width: `${pct}%`, backgroundColor: color }} />
                        </div>
                        {sa.evidence && <p>{sa.evidence}</p>}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}

          {/* Row 3: Key strengths + Areas to sharpen + Recommended next steps */}
          <div className="feedback-row feedback-row-recommendations">
            <div className="feedback-sections">
              <div className="feedback-section">
                <h3>Key strengths</h3>
                <ul>
                  {safeStrengths.length > 0 ? safeStrengths.map((item, idx) => (
                    <li key={idx}>{item}</li>
                  )) : <li>Focus on the improvement areas to unlock your strengths.</li>}
                </ul>
              </div>

              <div className="feedback-section">
                <h3>Areas to sharpen</h3>
                <ul>
                  {safeAreas.map((item, idx) => (
                    <li key={idx}>{item}</li>
                  ))}
                </ul>
              </div>

              <div className="feedback-section">
                <h3>Recommended next steps</h3>
                <ul>
                  {safeRecommendations.map((item, idx) => (
                    <li key={idx}>{item}</li>
                  ))}
                </ul>
              </div>
            </div>
          </div>

          {/* Row 4: Transcript */}
          <div className="feedback-row feedback-row-transcript">
            <div className="question-card">
              <div className="section-heading">
                <p className="eyebrow">Live transcript & conversation history</p>
                <h3>{hasChatTranscript ? 'Conversation' : 'Question & answer timeline'}</h3>
              </div>
              {hasChatTranscript ? (
                <div className="feedback-chat-thread" role="log" aria-label="Interview conversation">
                  {resolvedInterviewMessages.map((msg) => {
                    const isInterviewer = msg.sender === 'ai';
                    const timeLabel = formatChatTimestamp(msg.timestamp);
                    return (
                      <div
                        key={msg.id}
                        className={`feedback-chat-row ${isInterviewer ? 'feedback-chat-row--interviewer' : 'feedback-chat-row--candidate'}`}
                      >
                        <div className="feedback-chat-block">
                          <span className="feedback-chat-role">{isInterviewer ? 'Interviewer' : 'Candidate'}</span>
                          <div
                            className={`feedback-chat-bubble ${isInterviewer ? 'feedback-chat-bubble--interviewer' : 'feedback-chat-bubble--candidate'}`}
                          >
                            <p className="feedback-chat-text">{msg.text}</p>
                          </div>
                          {timeLabel ? <span className="feedback-chat-meta">{timeLabel}</span> : null}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="question-list">
                  {individualFeedback.length === 0 ? (
                    <p className="knob-description" style={{ padding: '16px 0' }}>
                      No question and answer pairs were captured for this session.
                    </p>
                  ) : null}
                  {individualFeedback.map((qa) => (
                    <div key={qa.question_number} className="question-wrapper">
                      <button
                        className={`question-row ${(selectedQuestion === qa.question_number || expandedQuestions.has(qa.question_number)) ? 'active' : ''}`}
                        onClick={() => setSelectedQuestion(qa.question_number)}
                      >
                        <span className="question-number">{qa.question_number}</span>
                        <span className="question-text">
                          {qa.question}
                        </span>
                      </button>
                      {(selectedQuestion === qa.question_number || expandedQuestions.has(qa.question_number)) && (
                        <div className="question-details">
                          <p className="question-response"><strong>Response:</strong> {qa.response}</p>
                          <div className="question-scores">
                            <div><span>Relevance</span><strong>{qa.analysis.relevance_score}/10</strong></div>
                            <div><span>Depth</span><strong>{qa.analysis.depth_score}/10</strong></div>
                            <div><span>Clarity</span><strong>{qa.analysis.communication_clarity}/10</strong></div>
                          </div>
                          <p className="question-feedback">{qa.analysis.feedback_summary}</p>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FeedbackDashboard;
